import { useEffect, useState } from 'react';
import {
  CreditCard,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ReceiptText,
  Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MoneyValue } from '../MoneyValue';
import { useEdcSocket } from '@/hooks/useEdcSocket';
import { resolveEdcTransaction } from '@/api/edc.api';
import type { SocketConfig } from '@/types/sale';
import type { ApiError } from '@/types/api';
import type { EdcState } from '@/types/payment';

interface EdcPanelProps {
  total: number;
  socketConfig: SocketConfig | null;
  /** PaymentTransaction id — the handle for resolving a lost response. */
  paymentTransactionId: string | null;
  onSuccess: () => void;
  onCancel: () => void;
  /**
   * Retry this cart. Receives the SERVER's verdict on whether a genuinely new
   * charge is safe — the caller needs it because only it holds the
   * idempotency key. Undefined means not known, which is treated as not safe.
   */
  onRetry: (retrySafe?: boolean) => void;
  /**
   * DEV ONLY — force a phase so the flow can be reviewed without a terminal.
   * Some phases (notably `unresolved`, which needs a terminal that accepts a
   * card and then goes silent) are otherwise near-impossible to reproduce.
   * `import.meta.env.DEV` is compile-time, so this is stripped from a
   * production build entirely.
   */
  __previewPhase?: EdcState['phase'];
}

/** The receipt trace number is exactly six digits (spec §7.2, an6 numeric). */
const TRACE_PATTERN = /^\d{6}$/;

/**
 * Card payment at the Permata EDC terminal.
 *
 * Mirrors EmoneyPanel's shape so a cashier who knows one knows the other, with
 * one screen that has no equivalent anywhere else in the app: the resolution
 * prompt shown when the terminal never answered.
 */
export function EdcPanel({
  total,
  socketConfig,
  paymentTransactionId,
  onSuccess,
  onCancel,
  onRetry,
  __previewPhase,
}: EdcPanelProps) {
  const live = useEdcSocket({ config: socketConfig, open: Boolean(socketConfig) });
  const state: EdcState =
    import.meta.env.DEV && __previewPhase
      ? {
          phase: __previewPhase,
          panMasking: '460502******1234',
          approvalCode: '000000',
          reason: 'Mesin EDC tidak menjawab sebelum batas waktu.',
          message: 'Status mesin EDC tidak terpantau. Cek mesin EDC.',
        }
      : live.state;

  useEffect(() => {
    if (import.meta.env.DEV && __previewPhase) return;
    if (state.phase === 'approved') {
      const t = setTimeout(onSuccess, 900);
      return () => clearTimeout(t);
    }
  }, [state.phase, onSuccess, __previewPhase]);

  return (
    <div className="flex flex-col items-center gap-5 py-4 text-center">
      <div className="rounded-lg bg-secondary px-6 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">Total tagihan</p>
        <MoneyValue value={total} emphasis className="text-3xl" />
      </div>

      {(state.phase === 'connecting' || state.phase === 'waiting_card') && (
        <div className="flex flex-col items-center gap-3">
          <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-primary/10">
            <span className="absolute inset-0 animate-ping rounded-full bg-primary/15" />
            <CreditCard className="h-12 w-12 text-primary" />
          </div>
          <p className="font-display text-lg font-semibold text-ink-900">
            {state.phase === 'connecting' ? 'Menghubungkan mesin EDC…' : 'Masukkan kartu di mesin EDC'}
          </p>
          <p className="text-sm text-muted-foreground">
            Pelanggan memasukkan atau menempelkan kartu, lalu mengetik PIN.
          </p>
        </div>
      )}

      {state.phase === 'processing' && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-14 w-14 animate-spin text-primary" />
          <p className="font-display text-lg font-semibold text-ink-900">Memproses di mesin EDC…</p>
          <p className="text-sm text-muted-foreground">Jangan cabut kartu atau matikan mesin.</p>
        </div>
      )}

      {state.phase === 'approved' && (
        <div className="flex animate-gold-pulse flex-col items-center gap-2 rounded-full p-4">
          <CheckCircle2 className="h-16 w-16 text-success" />
          <p className="font-display text-lg font-semibold text-success">Pembayaran berhasil</p>
          {state.panMasking && (
            <p className="font-mono text-sm text-ink-600">{state.panMasking}</p>
          )}
          {state.approvalCode && (
            <p className="text-xs text-muted-foreground">Kode approval {state.approvalCode}</p>
          )}
        </div>
      )}

      {state.phase === 'declined' && (
        <div className="flex flex-col items-center gap-2">
          <XCircle className="h-14 w-14 text-destructive" />
          <p className="font-display text-lg font-semibold text-destructive">Pembayaran ditolak</p>
          <p className="text-sm text-muted-foreground">
            {state.reason ?? 'Mesin EDC menolak transaksi ini.'}
          </p>
          <p className="text-sm text-ink-600">Kartu pelanggan tidak terdebit. Coba kartu lain.</p>
        </div>
      )}

      {state.phase === 'error' && (
        <div className="flex flex-col items-center gap-2">
          <AlertTriangle className="h-14 w-14 text-warning" />
          <p className="font-display text-lg font-semibold text-ink-900">Status tidak terpantau</p>
          <p className="text-sm text-muted-foreground">{state.message}</p>
        </div>
      )}

      {state.phase === 'unresolved' && (
        <EdcResolutionPrompt
          paymentTransactionId={paymentTransactionId}
          reason={state.reason}
          onSettled={onSuccess}
          onFailed={onCancel}
        />
      )}

      {/* No cancel button while the terminal has the card: the protocol has no
          cancel message, so a button here could not do what it says. */}
      {state.phase !== 'unresolved' && (
        <div className="flex w-full justify-center gap-2 pt-2">
          {(state.phase === 'declined' || state.phase === 'error') && (
            <>
              <Button variant="outline" onClick={onCancel}>
                Batalkan
              </Button>
              <Button onClick={() => onRetry(live.retrySafe)}>Coba lagi</Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The terminal never answered.
 *
 * This is the highest-stakes screen in the app and it is deliberately unlike
 * every other one:
 *
 *  - It is AMBER, not red. Red means failed, and the entire point is that this
 *    is not a failure — the card may well have been charged. Styling it as an
 *    error would teach exactly the wrong reflex.
 *  - It does not spin. Nothing is loading; the system has stopped and a person
 *    has to look at a physical machine. Motion here would imply otherwise.
 *  - The two answers carry deliberately different friction. Saying "no" is one
 *    tap. Saying "yes, it approved" requires typing the six digits off the
 *    printed slip — which cannot be done without having the slip in hand. That
 *    friction is the control, not an inconvenience.
 *  - Each button states its consequence, so nobody has to guess which is safe.
 */
function EdcResolutionPrompt({
  paymentTransactionId,
  reason,
  onSettled,
  onFailed,
}: {
  paymentTransactionId: string | null;
  reason?: string;
  onSettled: () => void;
  onFailed: () => void;
}) {
  const [answer, setAnswer] = useState<'none' | 'approved' | 'declined'>('none');
  const [traceNo, setTraceNo] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  const traceValid = TRACE_PATTERN.test(traceNo);
  const showTraceError = touched && !traceValid;

  async function submit(outcome: 'SETTLED' | 'FAILED') {
    if (!paymentTransactionId) {
      toast.error('Transaksi tidak dikenali. Hubungi supervisor.');
      return;
    }
    setBusy(true);
    try {
      await resolveEdcTransaction(paymentTransactionId, {
        outcome,
        receiptTraceNo: outcome === 'SETTLED' ? traceNo : undefined,
      });
      if (outcome === 'SETTLED') {
        toast.success('Pembayaran dicatat. Tiket akan dicetak.');
        onSettled();
      } else {
        toast.success('Pesanan dibatalkan.');
        onFailed();
      }
    } catch (error) {
      const apiErr = error as ApiError;
      toast.error(apiErr?.message ?? 'Gagal menyimpan. Coba lagi atau hubungi supervisor.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="w-full rounded-xl border-2 border-warning/40 bg-warning/5 p-5 text-left">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-warning" aria-hidden="true" />
        <div>
          <h3 className="font-display text-lg font-semibold text-ink-900">
            Mesin EDC tidak memberi jawaban
          </h3>
          <p className="mt-1 text-sm text-ink-600">
            Kartu pelanggan <strong>mungkin sudah terdebit</strong>. Jangan ulangi pembayaran
            sebelum memeriksa mesin.
          </p>
          {reason && <p className="mt-1 text-xs text-muted-foreground">({reason})</p>}
        </div>
      </div>

      <p className="mt-5 font-display text-base font-semibold text-ink-900">
        Lihat mesin EDC sekarang — apakah struk tercetak APPROVED?
      </p>

      {answer === 'none' && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setAnswer('approved')}
            // Deliberately the SAME visual weight as the other option. An
            // earlier pass tinted this one green, which read as "recommended" —
            // the last thing to suggest when the question is whether a customer
            // was charged. The icons carry the meaning; neither is a default.
            className="flex flex-col items-start gap-1 rounded-lg border-2 border-ink-200 bg-background p-4 text-left transition hover:border-ink-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-400"
          >
            <span className="flex items-center gap-2 font-semibold text-ink-900">
              <ReceiptText className="h-5 w-5 text-success" aria-hidden="true" />
              Ya — struk APPROVED
            </span>
            <span className="text-xs text-muted-foreground">
              Perlu nomor trace dari struk. Tiket akan dicetak.
            </span>
          </button>

          <button
            type="button"
            onClick={() => setAnswer('declined')}
            className="flex flex-col items-start gap-1 rounded-lg border-2 border-ink-200 bg-background p-4 text-left transition hover:border-ink-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-400"
          >
            <span className="flex items-center gap-2 font-semibold text-ink-900">
              <Ban className="h-5 w-5 text-ink-600" aria-hidden="true" />
              Tidak — tidak ada struk
            </span>
            <span className="text-xs text-muted-foreground">
              Atau struk tertulis DECLINE. Pesanan dibatalkan.
            </span>
          </button>
        </div>
      )}

      {answer === 'approved' && (
        <div className="mt-4 space-y-3">
          <div>
            <Label htmlFor="edc-trace">Nomor trace pada struk (6 digit)</Label>
            <Input
              id="edc-trace"
              inputMode="numeric"
              autoFocus
              maxLength={6}
              placeholder="000123"
              value={traceNo}
              onChange={(e) => setTraceNo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onBlur={() => setTouched(true)}
              aria-invalid={showTraceError}
              aria-describedby={showTraceError ? 'edc-trace-error' : 'edc-trace-hint'}
              className="mt-1 font-mono text-lg tracking-widest"
            />
            {showTraceError ? (
              <p id="edc-trace-error" className="mt-1 text-xs font-medium text-destructive">
                Masukkan 6 digit angka persis seperti tercetak pada struk.
              </p>
            ) : (
              <p id="edc-trace-hint" className="mt-1 text-xs text-muted-foreground">
                Tertulis sebagai TRACE atau NO. URUT pada struk mesin EDC.
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setAnswer('none')} disabled={busy}>
              Kembali
            </Button>
            <Button onClick={() => submit('SETTLED')} disabled={!traceValid || busy}>
              {busy ? 'Menyimpan…' : 'Catat sebagai lunas'}
            </Button>
          </div>
        </div>
      )}

      {answer === 'declined' && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-ink-600">
            Pesanan akan dibatalkan dan kursi dilepas. Pastikan mesin EDC benar-benar tidak
            mencetak struk APPROVED.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setAnswer('none')} disabled={busy}>
              Kembali
            </Button>
            <Button variant="destructive" onClick={() => submit('FAILED')} disabled={busy}>
              {busy ? 'Menyimpan…' : 'Batalkan pesanan'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
